import { create } from 'zustand';
import type { ColumnSchema, DucklingTier, QueryError, QueryStats, ResultRow } from '@/types/api';
import { ResultCache } from '@/cache/ResultCache';
import {
  DEFAULT_MAX_CACHE_ROWS,
  type LocalQueryResult,
  type FilterSpec,
  type GridMode,
  type LocalEngine,
  type SortSpec,
  type ViewSpec,
} from '@/cache/types';
import type { ColumnProfile } from '@/cache/stats';
import type { GroupState } from '@/components/group/groupModel';
import type { DistinctValue } from '@/cache/ResultCache';
import {
  asQueryError,
  createRemoteExecutor,
  executeQuery,
  QueryCancelled,
} from './execute';

export const PAGE_SIZE = 500;

export type RunStatus = 'idle' | 'running' | 'success' | 'error' | 'cancelled';

export interface CellResult {
  readonly status: RunStatus;
  readonly queryId: string | null;
  readonly sql: string;
  readonly columns: readonly ColumnSchema[];
  readonly rows: readonly ResultRow[];
  readonly totalRows: number;
  readonly error: QueryError | null;
  readonly stats: QueryStats | null;
  readonly mode: GridMode;
  readonly modeReason: string | null;
  /** Wall time of the last view (sort/filter/page) interaction, in ms. */
  readonly lastViewMs: number | null;
  readonly progressRows: number;
  readonly sort: readonly SortSpec[];
  readonly filters: readonly FilterSpec[];
  /** Raw predicate from the panel's Text mode; replaces `filters` when set. */
  readonly predicate: string | null;
  readonly group: GroupState | null;
  /** Schema of a grouped view; the grid uses this instead of `columns`. */
  readonly groupedColumns: readonly ColumnSchema[] | null;
  readonly profiles: Readonly<Record<string, ColumnProfile>>;
  readonly hiddenColumns: readonly string[];
  readonly columnOrder: readonly string[];
  readonly columnWidths: Readonly<Record<string, number>>;
}

export const EMPTY_RESULT: CellResult = {
  status: 'idle',
  queryId: null,
  sql: '',
  columns: [],
  rows: [],
  totalRows: 0,
  error: null,
  stats: null,
  mode: 'idle',
  modeReason: null,
  lastViewMs: null,
  progressRows: 0,
  sort: [],
  filters: [],
  predicate: null,
  group: null,
  groupedColumns: null,
  profiles: {},
  hiddenColumns: [],
  columnOrder: [],
  columnWidths: {},
};

/** Non-reactive per-cell resources. Kept out of the store so React never
 *  diffs a WASM connection. */
interface CellResources {
  cache: ResultCache;
  controller: AbortController | null;
}

const resources = new Map<string, CellResources>();

/** Overridable for tests: swaps DuckDB-WASM for a fake engine. */
let engineFactory: () => Promise<LocalEngine> = async () => {
  const module = await import('@/cache/duckdbEngine');
  return module.getDuckDbEngine();
};

export function setEngineFactory(factory: () => Promise<LocalEngine>): void {
  engineFactory = factory;
}

function maxCacheRows(): number {
  const raw = import.meta.env.VITE_MAX_CACHE_ROWS;
  const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_CACHE_ROWS;
}

interface ResultState {
  results: Record<string, CellResult>;
  tier: DucklingTier;

  get: (cellId: string) => CellResult;
  setTier: (tier: DucklingTier) => void;
  run: (cellId: string, sql: string) => Promise<void>;
  cancel: (cellId: string) => void;
  setSort: (cellId: string, sort: readonly SortSpec[]) => Promise<void>;
  setFilters: (cellId: string, filters: readonly FilterSpec[]) => Promise<void>;
  applyFilterSort: (
    cellId: string,
    filters: readonly FilterSpec[],
    sort: readonly SortSpec[],
    predicate: string | null,
  ) => Promise<void>;
  applyGroup: (cellId: string, group: GroupState | null) => Promise<void>;
  distinctValues: (cellId: string, column: string) => Promise<readonly DistinctValue[] | null>;
  loadPage: (cellId: string, offset: number) => Promise<void>;
  loadProfile: (cellId: string, column: ColumnSchema) => Promise<void>;
  toggleColumn: (cellId: string, column: string) => void;
  setColumnOrder: (cellId: string, order: readonly string[]) => void;
  setColumnWidths: (cellId: string, widths: Readonly<Record<string, number>>) => void;
  exportParquet: (cellId: string) => Promise<Uint8Array | null>;
  /** Full current view for a client-side export; null when not cached. */
  exportData: (cellId: string) => Promise<LocalQueryResult | null>;
  reset: (cellId: string) => void;
}

export const useResultStore = create<ResultState>()((set, get) => {
  const patch = (cellId: string, changes: Partial<CellResult>): void => {
    set((state) => ({
      results: {
        ...state.results,
        [cellId]: { ...(state.results[cellId] ?? EMPTY_RESULT), ...changes },
      },
    }));
  };

  const resourcesFor = (cellId: string): CellResources => {
    const existing = resources.get(cellId);
    if (existing) return existing;

    const cache = new ResultCache({
      maxRows: maxCacheRows(),
      engineFactory: () => engineFactory(),
      remote: createRemoteExecutor(get().tier),
      onStatusChange: (status) => patch(cellId, { mode: status.mode, modeReason: status.reason }),
    });

    const created: CellResources = { cache, controller: null };
    resources.set(cellId, created);
    return created;
  };

  const refreshView = async (cellId: string, offset: number): Promise<void> => {
    const current = get().get(cellId);
    const { cache } = resourcesFor(cellId);
    const spec: ViewSpec = {
      sort: current.sort,
      filters: current.filters,
      predicate: current.predicate,
      group: current.group,
      offset,
      limit: PAGE_SIZE,
    };

    try {
      const view = await cache.view(spec);
      patch(cellId, {
        rows: view.rows,
        totalRows: view.totalRows,
        lastViewMs: view.elapsedMs,
        groupedColumns: view.columns ?? null,
      });
    } catch (cause) {
      patch(cellId, { status: 'error', error: asQueryError(cause) });
    }
  };

  return {
    results: {},
    tier: 'small',

    get: (cellId) => get().results[cellId] ?? EMPTY_RESULT,

    setTier: (tier) => set({ tier }),

    run: async (cellId, sql) => {
      const trimmed = sql.trim();
      if (trimmed === '') {
        patch(cellId, {
          ...EMPTY_RESULT,
          status: 'error',
          sql,
          error: {
            message: 'Nothing to run. This cell is empty.',
            sqlState: '42601',
            position: null,
            detail: null,
            hint: null,
          },
        });
        return;
      }

      const cellResources = resourcesFor(cellId);
      cellResources.controller?.abort();
      const controller = new AbortController();
      cellResources.controller = controller;

      const previous = get().get(cellId);
      patch(cellId, {
        ...EMPTY_RESULT,
        status: 'running',
        sql: trimmed,
        mode: 'idle',
        // Keep sort/filter across re-runs of the same SQL so a refresh does not
        // throw away the user's view.
        sort: previous.sql === trimmed ? previous.sort : [],
        filters: previous.sql === trimmed ? previous.filters : [],
        predicate: previous.sql === trimmed ? previous.predicate : null,
        group: previous.sql === trimmed ? previous.group : null,
        hiddenColumns: previous.sql === trimmed ? previous.hiddenColumns : [],
        columnOrder: previous.sql === trimmed ? previous.columnOrder : [],
        columnWidths: previous.sql === trimmed ? previous.columnWidths : {},
      });

      try {
        const limit = maxCacheRows();
        const result = await executeQuery(trimmed, {
          tier: get().tier,
          // Ask for one row past the cache limit so truncation is detectable
          // even when the server does not set the flag.
          maxRows: limit + 1,
          signal: controller.signal,
          onSchema: (columns) => patch(cellId, { columns }),
          onBatch: (_rows, total) => patch(cellId, { progressRows: total }),
        });

        const overLimit = result.rows.length > limit;
        await cellResources.cache.load({
          queryId: result.queryId,
          sql: trimmed,
          columns: result.columns,
          rows: overLimit ? result.rows.slice(0, limit) : result.rows,
          truncated: result.truncated || overLimit,
        });

        patch(cellId, {
          status: 'success',
          queryId: result.queryId,
          columns: result.columns,
          stats: result.stats,
          columnOrder: result.columns.map((column) => column.name),
        });

        await refreshView(cellId, 0);
      } catch (cause) {
        if (cause instanceof QueryCancelled || controller.signal.aborted) {
          patch(cellId, { status: 'cancelled', mode: 'idle' });
          return;
        }
        patch(cellId, {
          status: 'error',
          error: asQueryError(cause),
          rows: [],
          totalRows: 0,
          mode: 'idle',
        });
      } finally {
        if (cellResources.controller === controller) cellResources.controller = null;
      }
    },

    cancel: (cellId) => {
      const cellResources = resources.get(cellId);
      cellResources?.controller?.abort();
    },

    setSort: async (cellId, sort) => {
      patch(cellId, { sort });
      await refreshView(cellId, 0);
    },

    setFilters: async (cellId, filters) => {
      patch(cellId, { filters });
      await refreshView(cellId, 0);
    },

    applyFilterSort: async (cellId, filters, sort, predicate) => {
      patch(cellId, { filters, sort, predicate });
      await refreshView(cellId, 0);
    },

    applyGroup: async (cellId, group) => {
      // Column order and hidden columns describe the ungrouped schema, so they
      // are meaningless once the shape changes.
      patch(cellId, { group, columnOrder: [], hiddenColumns: [] });
      await refreshView(cellId, 0);
    },

    distinctValues: async (cellId, column) => {
      const { cache } = resourcesFor(cellId);
      return cache.distinctValues(column);
    },

    loadPage: async (cellId, offset) => {
      await refreshView(cellId, offset);
    },

    loadProfile: async (cellId, column) => {
      const current = get().get(cellId);
      if (current.profiles[column.name]) return;
      const { cache } = resourcesFor(cellId);
      const profile = await cache.profile(column);
      if (!profile) return;
      set((state) => {
        const existing = state.results[cellId] ?? EMPTY_RESULT;
        return {
          results: {
            ...state.results,
            [cellId]: {
              ...existing,
              profiles: { ...existing.profiles, [column.name]: profile },
            },
          },
        };
      });
    },

    toggleColumn: (cellId, column) => {
      const current = get().get(cellId);
      const hidden = current.hiddenColumns.includes(column)
        ? current.hiddenColumns.filter((name) => name !== column)
        : [...current.hiddenColumns, column];
      patch(cellId, { hiddenColumns: hidden });
    },

    setColumnOrder: (cellId, columnOrder) => patch(cellId, { columnOrder }),

    setColumnWidths: (cellId, columnWidths) => patch(cellId, { columnWidths }),

    exportParquet: async (cellId) => {
      const { cache } = resourcesFor(cellId);
      return cache.exportParquet();
    },

    exportData: async (cellId) => {
      const current = get().get(cellId);
      const { cache } = resourcesFor(cellId);
      return cache.exportView({
        sort: current.sort,
        filters: current.filters,
        predicate: current.predicate,
        group: current.group,
      });
    },

    reset: (cellId) => {
      const cellResources = resources.get(cellId);
      if (cellResources) {
        cellResources.controller?.abort();
        void cellResources.cache.dispose();
        resources.delete(cellId);
      }
      set((state) => {
        const next = { ...state.results };
        delete next[cellId];
        return { results: next };
      });
    },
  };
});

/** Test hook: clears every cached engine and result. */
export function __resetResultStoreForTests(): void {
  for (const [, value] of resources) {
    value.controller?.abort();
  }
  resources.clear();
  useResultStore.setState({ results: {} });
}
